import { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';

import {
  DepartmentCollectionDTO,
  DepartmentTreeNodeDTO,
  useGetCollectionMembers,
  useGetCollectionsForDepartment,
  useGetCurrentDepartmentTree,
} from '@/api/trafikportalen';
import {
  collectionTypeToStudentType,
  COLLECTION_TYPE_LABELS,
  isStudentCollectionType,
  studentTypeToCollectionType,
} from '@/helpers/collectionTypes';
import { FormattedStudent, StudentType } from '@/types/students';

// The department tree rarely changes and is explicitly invalidated by every
// department mutation, so we keep it fresh for a while to avoid refetching it
// for every virtualized list row that mounts while scrolling.
const DEPARTMENT_TREE_STALE_TIME = 1000 * 60 * 5;

export const findDepartmentByCollectionType = (
  tree: DepartmentTreeNodeDTO,
  collectionType: string,
): DepartmentTreeNodeDTO | null => {
  if (tree.type === collectionType) return tree;
  for (const child of tree.children) {
    const found = findDepartmentByCollectionType(child, collectionType);
    if (found) return found;
  }
  return null;
};

const findDepartmentById = (
  tree: DepartmentTreeNodeDTO,
  id: string,
): DepartmentTreeNodeDTO | null => {
  if (tree.id === id) return tree;
  for (const child of tree.children) {
    const found = findDepartmentById(child, id);
    if (found) return found;
  }
  return null;
};

interface UseGetStudentCollection {
  students: FormattedStudent[] | undefined;
  collection: DepartmentCollectionDTO | null;
  collectionId: string | null;
  department: DepartmentTreeNodeDTO | null;
  isLoading: boolean;
}

export const useGetStudentCollection = (
  type: StudentType,
): UseGetStudentCollection => {
  const { data: departmentTree, isLoading: treeLoading } =
    useGetCurrentDepartmentTree({
      query: { staleTime: DEPARTMENT_TREE_STALE_TIME },
    });

  const collectionType = studentTypeToCollectionType(type);

  const department = useMemo(() => {
    if (!departmentTree) return null;
    return findDepartmentByCollectionType(departmentTree, collectionType);
  }, [departmentTree, collectionType]);

  const { data: collections, isLoading: collectionsLoading } =
    useGetCollectionsForDepartment(department?.id ?? '', {
      query: {
        enabled: !!department?.id,
      },
    });

  const collection = useMemo(() => {
    if (collectionsLoading || !collections) return null;

    return (
      collections.find((collection) => collection.type === collectionType) ||
      null
    );
  }, [collections, collectionsLoading, collectionType]);

  const { data: collectionMembers, isLoading: collectionMembersLoading } =
    useGetCollectionMembers(collection?.id ?? '', {
      query: {
        enabled: !!collection?.id,
      },
    });

  const isLoading = useMemo(() => {
    return treeLoading || collectionsLoading || collectionMembersLoading;
  }, [treeLoading, collectionsLoading, collectionMembersLoading]);

  const formattedStudents = useMemo((): FormattedStudent[] | undefined => {
    if (isLoading || !collectionMembers) return undefined;

    return collectionMembers.map((student) => ({
      name: `${student.traveller.first_name} ${student.traveller.last_name}`,
      ...student.traveller,
      ...student.collection_member,
    }));
  }, [isLoading, collectionMembers]);

  return {
    students: formattedStudents,
    collection,
    collectionId: collection?.id ?? null,
    department,
    isLoading,
  };
};

export const useGetDepartmentDisplayName = (): ((
  departmentId: string,
) => string) => {
  const { formatMessage } = useIntl();
  const { data: departmentTree } = useGetCurrentDepartmentTree({
    query: { staleTime: DEPARTMENT_TREE_STALE_TIME },
  });

  return useCallback(
    (departmentId: string) => {
      if (!departmentTree) return '';
      const department = findDepartmentById(departmentTree, departmentId);
      if (!department) return '';
      if (!isStudentCollectionType(department.type)) return department.name;
      return formatMessage(COLLECTION_TYPE_LABELS[department.type]) || '';
    },
    [departmentTree, formatMessage],
  );
};

export const useStudentCategoryType = (
  departmentId: string,
): StudentType | null => {
  const { data: departmentTree } = useGetCurrentDepartmentTree({
    query: { staleTime: DEPARTMENT_TREE_STALE_TIME },
  });

  const department = useMemo(() => {
    if (!departmentTree) return null;
    return findDepartmentById(departmentTree, departmentId);
  }, [departmentTree, departmentId]);

  return useMemo(() => {
    if (!department || !isStudentCollectionType(department.type)) return null;
    return collectionTypeToStudentType(department.type);
  }, [department]);
};
